import React, { useState, useCallback, useRef, useEffect } from "react"
import styled from "styled-components"
import { StoresList } from "../StoresList/StoresList"
import { PdfList } from "../PdfList/PdfList"
import { usePdfStores } from "../../hooks/usePdfStores"
import { usePdfList } from "../../hooks/usePdfList"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import { color, font } from "@smoothincorg/smooth-ui"

type StoreSelection = {
  storeId: number
  pop: boolean
  flyer: boolean
}

type ContainerProps = {}

type Props = ContainerProps & {
  className?: string
}

type ModalMessage = {
  title: string
  message: string
  type: "success" | "error" | "info"
}

const Component: React.VFC<Props> = (props) => {
  const { stores, loading, loadMore, hasMore, refresh, isRefreshing } = usePdfStores()
  const [isCreating, setIsCreating] = useState(false)
  const [showCreatingMessage, setShowCreatingMessage] = useState(false)
  const [selections, setSelections] = useState<StoreSelection[]>([])
  const [modalMessage, setModalMessage] = useState<ModalMessage | null>(null)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // コンポーネントのクリーンアップ
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [])

  // 新規ファイル検出時のコールバック
  const handleNewFileDetected = useCallback(() => {
    setShowCreatingMessage(false)
    // ポーリングを停止
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  const {
    pdfItems,
    loading: pdfLoading,
    mutate: mutatePdfList,
    isNewItem,
  } = usePdfList(handleNewFileDetected)
  const { postWithToken, getWithToken } = useSmoothApi()

  // ジョブステータスをポーリングする関数
  const pollJobStatus = useCallback(
    async (jobId: string) => {
      try {
        const statusUrl = `${SMOOTH_LINE_API_URL}/base_api/smoother/pdfs/status/${jobId}`
        const response = await getWithToken(statusUrl)

        if (response.status === 200) {
          const status = response.data.status

          if (status === "completed") {
            setShowCreatingMessage(false)
            mutatePdfList() // PDF一覧を再取得
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current)
              pollingIntervalRef.current = null
            }
          } else if (status === "failed") {
            setShowCreatingMessage(false)

            // エラーメッセージを様々なパスで試行（data.data.error_messageの構造に対応）
            let errorMessage = "詳細な情報がありません"

            if (
              response.data?.data?.errorMessage &&
              typeof response.data.data.errorMessage === "string" &&
              response.data.data.errorMessage.trim()
            ) {
              errorMessage = response.data.data.errorMessage
            } else if (
              response.data?.errorMessage &&
              typeof response.data.errorMessage === "string" &&
              response.data.errorMessage.trim()
            ) {
              errorMessage = response.data.errorMessage
            } else if (
              response.data?.data?.message &&
              typeof response.data.data.message === "string" &&
              response.data.data.message.trim()
            ) {
              errorMessage = response.data.data.message
            } else if (
              response.data?.message &&
              typeof response.data.message === "string" &&
              response.data.message.trim()
            ) {
              errorMessage = response.data.message
            }

            setModalMessage({
              title: "PDF作成エラー",
              message: `PDFの作成に失敗しました。\nエラー：${errorMessage}`,
              type: "error",
            })

            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current)
              pollingIntervalRef.current = null
            }
          }
          // status が 'processing' や 'pending' の場合は何もせず、次回のポーリングを待つ
        }
      } catch (error) {
        // ポーリングエラーは無視して継続
      }
    },
    [getWithToken, mutatePdfList]
  )

  const handleCreatePdf = async (selections: StoreSelection[]) => {
    const popStores = selections.filter((s) => s.pop).map((s) => s.storeId)
    const flyerStores = selections.filter((s) => s.flyer).map((s) => s.storeId)

    if (popStores.length === 0 && flyerStores.length === 0) {
      setModalMessage({
        title: "選択エラー",
        message: "加盟店を選択してください",
        type: "error",
      })
      return
    }

    setIsCreating(true)

    try {
      const url = `${SMOOTH_LINE_API_URL}/base_api/smoother/pdfs`
      const payload = {
        store_id_list_pop: popStores,
        store_id_list_flyer: flyerStores,
      }

      const response = await postWithToken(url, payload)

      // APIがcamelCaseに変更されたので、jobId (camelCase) をチェック
      if (response.status === 200 && response.data.jobId) {
        const jobId = response.data.jobId

        setModalMessage({
          title: "PDF作成開始",
          message:
            "PDFの作成を開始しました。完了すると下の一覧に表示されますのでしばらくお待ちください",
          type: "success",
        })

        // 「PDFを作成中」メッセージを表示
        setShowCreatingMessage(true)

        // ポーリング開始（5秒間隔）
        pollingIntervalRef.current = setInterval(() => {
          pollJobStatus(jobId)
        }, 5000)

        // 初回ポーリング
        pollJobStatus(jobId)
      } else {
        setModalMessage({
          title: "リクエストエラー",
          message: "PDFの作成リクエストに失敗しました",
          type: "error",
        })
      }
    } catch (error: any) {
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || "リクエストが正しくありません"
        setModalMessage({
          title: "エラー",
          message: `エラー: ${errorMessage}`,
          type: "error",
        })
      } else {
        setModalMessage({
          title: "PDF作成エラー",
          message: "PDFの作成に失敗しました。しばらくしてからもう一度お試しください。",
          type: "error",
        })
      }
    } finally {
      setIsCreating(false)
    }
  }

  const handleDownload = async (key: string) => {
    try {
      const url = `${SMOOTH_LINE_API_URL}/base_api/smoother/pdfs/download?key=${key}`

      const response = await getWithToken(url)

      if (response.status === 200 && response.data.downloadUrl) {
        // downloadUrlを使ってダウンロードを開始
        const downloadUrl = response.data.downloadUrl

        // downloadUrlの検証（環境に応じたプロトコルチェック）
        const isDev = process.env.NODE_ENV === "development"
        const isValidUrl =
          downloadUrl &&
          (downloadUrl.startsWith("https://") || (isDev && downloadUrl.startsWith("http://")))

        if (!isValidUrl) {
          setModalMessage({
            title: "セキュリティエラー",
            message:
              "無効なダウンロードURLです。セキュリティ上の理由によりダウンロードを中止しました。",
            type: "error",
          })
          return
        }

        const link = document.createElement("a")
        link.href = downloadUrl
        link.download = "" // ファイル名はサーバーから取得
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        setModalMessage({
          title: "ダウンロードエラー",
          message: "ダウンロードURLの取得に失敗しました",
          type: "error",
        })
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setModalMessage({
          title: "ファイルエラー",
          message: "指定されたPDFファイルが見つかりません",
          type: "error",
        })
      } else {
        setModalMessage({
          title: "ダウンロードエラー",
          message: "PDFのダウンロードに失敗しました。しばらくしてからもう一度お試しください。",
          type: "error",
        })
      }
    }
  }

  if (loading || pdfLoading) {
    return (
      <div className={`${props.className}-loading`}>
        <div className={`${props.className}-loading-content`}>
          <div className={`${props.className}-loading-spinner`}></div>
          <span>読み込み中...</span>
        </div>
      </div>
    )
  }

  return (
    <div className={props.className}>
      {showCreatingMessage && (
        <div className={`${props.className}-creating-message`}>
          <div className={`${props.className}-creating-content`}>
            <span>PDFを作成中...</span>
            <div className={`${props.className}-spinner`}></div>
          </div>
        </div>
      )}

      <StoresList
        stores={stores}
        onLoadMore={loadMore}
        hasMore={hasMore}
        onCreatePdf={handleCreatePdf}
        onRefresh={refresh}
        isCreating={isCreating}
        isRefreshing={isRefreshing}
        selections={selections}
        onSelectionsChange={setSelections}
      />

      {showCreatingMessage && (
        <div className={`${props.className}-creating-message`}>
          <div className={`${props.className}-creating-content`}>
            <span>PDFを作成中...</span>
            <div className={`${props.className}-spinner`}></div>
          </div>
        </div>
      )}

      <PdfList pdfItems={pdfItems} onDownload={handleDownload} isNewItem={isNewItem} />

      {modalMessage && (
        <div className={`${props.className}-modal-overlay`}>
          <div className={`${props.className}-modal`}>
            <div
              className={`${props.className}-modal-header ${props.className}-modal-header--${modalMessage.type}`}
            >
              <h3>{modalMessage.title}</h3>
            </div>
            <div className={`${props.className}-modal-body`}>
              <p>{modalMessage.message}</p>
            </div>
            <div className={`${props.className}-modal-footer`}>
              <button
                className={`${props.className}-modal-button`}
                onClick={() => setModalMessage(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const StyledComponent = styled(Component)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  &-creating-message {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
    text-align: center;
  }

  &-creating-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    ${font(16)};
    font-weight: bold;
    color: #856404;
  }

  &-spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #ffeaa7;
    border-top: 3px solid #856404;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  &-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }

  &-loading-content {
    display: flex;
    align-items: center;
    gap: 15px;
    ${font(16)};
  }

  &-loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid ${color.main};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  &-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  &-modal {
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    max-width: 500px;
    min-width: 300px;
    margin: 20px;
    overflow: hidden;
  }

  &-modal-header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #e9ecef;

    &--success {
      background-color: #d4edda;
      color: #155724;
    }

    &--error {
      background-color: #f8d7da;
      color: #721c24;
    }

    &--info {
      background-color: #cce7ff;
      color: #004085;
    }

    h3 {
      margin: 0;
      ${font(18)};
      font-weight: bold;
    }
  }

  &-modal-body {
    padding: 20px 24px;

    p {
      margin: 0;
      ${font(14)};
      line-height: 1.5;
      color: ${color.darkGray};
      white-space: pre-line;
    }
  }

  &-modal-footer {
    padding: 16px 24px 20px;
    text-align: right;
    border-top: 1px solid #e9ecef;
  }

  &-modal-button {
    background-color: ${color.main};
    color: white;
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    ${font(14)};
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${color.darkSkyblue};
    }

    &:focus {
      outline: 2px solid ${color.main};
      outline-offset: 2px;
    }
  }
`

const Container: React.FC<ContainerProps> = (props) => {
  return <StyledComponent {...props} />
}

export const PdfContainer = Container
